import React, { useEffect, useRef, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import TabPanel from "@mui/lab/TabPanel";
import { TabContext } from "@mui/lab";
import { TextField } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

import "./Section.css";
import SuccessAlert from "../components/success-alert/SuccessAlert";
import ErrorAlert from "../components/error-alert/ErrorAlert";
import useLanguages from "../hooks/useLanguages";
import { API, SERVER_DOMAIN } from "../assets/staticData";
import { toast } from "react-toastify";
import TextEditor from "../components/rich-text/TextEditor/TextEditor";
import { CACHE_KEY } from "../constant/cache-key";
import ClearCacheButton from "../components/ClearCacheButton/ClearCacheButton";

export default function MC({ user }) {
  const langs = useLanguages();

  const [value, setValue] = useState("0");
  const [data, setData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [images, setImages] = useState([]);
  const [success, setSuccess] = useState(false);
  const [fail, setFail] = useState(false);
  const [loading, setLoading] = useState(true);
  const hiddenFileInput = useRef();

  let userToken = localStorage.getItem("userToken");

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  let tabStyle = { textTransform: "capitalize", fontWeight: "500" };

  // Get Data from backend
  useEffect(() => {
    let docs = langs.map((item) => ({
      header: "",
      body: "",
      language: item.attributes.language,
    }));
    setData(docs);

    let imgDocs = langs.map((item) => ({
      headerImg: "",
      language: item.attributes.language,
    }));
    setImages(imgDocs);

    async function getData() {
      await fetch(`${API}/mc`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      }).then(async (res) => {
        const response = await res.json();
        const apiData = response.data?.attributes?.data || [];

        setData((prevData) => {
          return prevData.map((localItem) => {
            const apiItem = apiData.find(
              (apiItem) => apiItem.language === localItem.language
            );

            if (apiItem) {
              return {
                ...localItem,
                header: apiItem.header || localItem.header,
                body: apiItem.body || localItem.body,
              };
            } else {
              return localItem;
            }
          });
        });

        const apiImages = response.data?.attributes?.images || [];

        setImages((prevData) => {
          return prevData.map((localItem) => {
            const apiItem = apiImages.find(
              (apiItem) => apiItem.language === localItem.language
            );

            if (apiItem) {
              return {
                ...localItem,
                headerImg: apiItem.headerImg || localItem.headerImg,
              };
            } else {
              return localItem;
            }
          });
        });

        setLoading(false);
      });
    }

    getData();
  }, [langs]);

  const submitData = () => {
    fetch(`${API}/mc`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({
        data: { data: data },
      }),
    }).then(async (res) => {
      if (res.ok) {
        setSuccess(true);
      } else {
        setFail(true);
      }
    });
  };

  const handleImageChange = (e, index, type) => {
    const formData = new FormData();
    const file = e.target.files[0];

    if (file) {
      formData.append("files", file);

      fetch(`${API}/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        body: formData,
      }).then(async (res) => {
        const response = await res.json();
        const iconURL = response[0]?.url;

        let updatedImages = [...images];

        updatedImages[index] = { ...updatedImages[index], [type]: iconURL };

        fetch(`${API}/mc`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({
            data: { images: updatedImages },
          }),
        }).then((res) => {
          if (res.ok) {
            setImages(updatedImages);
            toast("Updated Successfully", {
              position: "top-center",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
              type: "success",
            });
          }
        });
      });
    }
  };

  return (
    <TabContext value={value}>
      <Box sx={{ width: "100%" }}>
        <h3 style={{ textTransform: "capitalize" }}>
          Margin Calculator Settings
        </h3>

        <Box mt={2}>
          <ClearCacheButton
            keys={[
              CACHE_KEY.marginCalculator
            ]} />
        </Box>

        <Tabs
          value={value}
          onChange={handleChange}
          textColor="secondary"
          indicatorColor="secondary"
          aria-label="secondary tabs"
        >
          {langs?.map((item, index) => {
            return (
              <Tab
                key={index}
                sx={tabStyle}
                value={index.toString()}
                label={item.attributes.language}
                icon={
                  <img
                    src={
                      SERVER_DOMAIN + item.attributes.icon.data.attributes.url
                    }
                    style={{ width: 25, aspectRatio: 1, borderRadius: 20 }}
                  />
                }
                iconPosition="start"
              />
            );
          })}
        </Tabs>

        {loading ? (
          <></>
        ) : (
          <>
            {langs?.map((item, index) => {
              return (
                <TabPanel value={index.toString()} key={index}>
                  <SuccessAlert show={success} setShow={(v) => setSuccess(v)} />
                  <ErrorAlert show={fail} setShow={(v) => setFail(v)} />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 20,
                    }}
                  >
                    <div>
                      <p style={{ fontSize: 15, marginBottom: 8 }}>
                        Header Text
                      </p>
                      <div style={{ margin: "10px 0" }}>
                        <TextEditor
                          value={data[index]?.header || ""}
                          onChange={(v) => {
                            setData([
                              ...data.map((item, idx) => {
                                if (idx === index) {
                                  return {
                                    ...item,
                                    header: v,
                                  };
                                } else {
                                  return item;
                                }
                              }),
                            ]);
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <p style={{ fontSize: 15, marginBottom: 8 }}>Body Text</p>

                      <div style={{ margin: "10px 0" }}>
                        <TextEditor
                          value={data[index]?.body || ""}
                          onChange={(v) => {
                            setData([
                              ...data.map((item, idx) => {
                                if (idx === index) {
                                  return {
                                    ...item,
                                    body: v,
                                  };
                                } else {
                                  return item;
                                }
                              }),
                            ]);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {user.perms[16].edit && (
                    <>
                      <div
                        className="submitBTN"
                        onClick={() => submitData(item, index)}
                      >
                        Save Changes
                      </div>

                      <div className="divider"></div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          gap: 25,
                          flexWrap: "wrap",
                        }}
                      >
                        <div className="w-4/12">
                          <h3 className="ml-3">Header Image</h3>

                          <div
                            className="group relative w-full mt-4 rounded-[15px] overflow-hidden cursor-pointer "
                            onClick={() => hiddenFileInput.current.click()}
                          >
                            <img
                              style={{
                                width: "100%",
                                aspectRatio: 1,
                                objectFit: "cover",
                                transition: "0.2s",
                              }}
                              src={
                                images[index]?.headerImg
                                  ? SERVER_DOMAIN + images[index]?.headerImg
                                  : require("../assets/placeholder-image.png")
                              }
                            />

                            <div className="group absolute top-0 left-0 w-full rounded-[15px] h-full bg-black bg-opacity-0 hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                              <CameraAltIcon
                                style={{ fontSize: 45 }}
                                className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              />
                            </div>

                            <input
                              type="file"
                              accept="image/png, image/jpeg"
                              onChange={(e) =>
                                handleImageChange(e, index, "headerImg")
                              }
                              ref={hiddenFileInput}
                              style={{ display: "none" }}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </TabPanel>
              );
            })}
          </>
        )}
      </Box>
    </TabContext>
  );
}
